'use client';

import { c as _c } from "react/compiler-runtime";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import type { HofDashboardData } from '@/features/hof-dashboard/api/hof-dashboard-data';
import { AreaView } from '@/features/hof-dashboard/components/area-view';
import { DashboardTabList } from '@/features/hof-dashboard/components/dashboard-tabs';
import { Panel, PRIMARY_BUTTON_CLASS, SECONDARY_BUTTON_CLASS } from '@/features/hof-dashboard/components/dashboard-ui';
import { DocumentsView } from '@/features/hof-dashboard/components/documents-view';
import { HofOverviewTable } from '@/features/hof-dashboard/components/hof-overview-table';
import { OverviewView } from '@/features/hof-dashboard/components/overview-view';
import { HOF_DASHBOARD_AREA_LABELS, type HofDashboardArea } from '@/features/hof-dashboard/constants';
import { useRememberedHofId } from '@/features/hof-dashboard/hooks/use-remembered-hof-id';
import { useScrollToForm } from '@/features/hof-dashboard/hooks/use-scroll-to-form';
import { useTabInAddress } from '@/features/hof-dashboard/hooks/use-tab-in-address';
import { hasUnsavedWork } from '@/features/hof-dashboard/hooks/use-warn-before-leaving';
import { text, translate } from '@/features/hof-dashboard/texts';
import { scrollBehavior } from '@/features/hof-dashboard/utils/scroll-behavior';
import { getSubmissionProgress, isOpen, type SubmissionProgress } from '@/features/hof-dashboard/utils/submission-progress';
import { useReleasePushSubscriptions } from '@/hooks/use-release-push-subscriptions';
import { flushPersonalData } from '@/lib/flush-personal-data';
import { trpc } from '@/trpc/client';
import type { Locale, StaticTranslationString } from '@/types/types';
import { i18nConfig } from '@/types/types';
import { TabGroup, TabPanel, TabPanels } from '@headlessui/react';
import { LogOut } from 'lucide-react';
import { signIn, signOut, useSession } from 'next-auth/react';
import { useCurrentLocale } from 'next-i18n-router/client';
import type React from 'react';
import { useCallback, useId, useMemo, useRef, useState } from 'react';
type Tab = 'overview' | HofDashboardArea | 'documents';
const TABS: {
  id: Tab;
  label: StaticTranslationString;
}[] = [{
  id: 'overview',
  label: text.tabOverview
}, {
  id: 'infrastructure',
  label: HOF_DASHBOARD_AREA_LABELS.infrastructure
}, {
  id: 'program',
  label: HOF_DASHBOARD_AREA_LABELS.program
}, {
  id: 'material',
  label: HOF_DASHBOARD_AREA_LABELS.material
}, {
  id: 'documents',
  label: text.tabDocuments
}];
const TAB_IDS = TABS.map(({
  id
}) => id);
const LoadingState: React.FC<{
  locale: Locale;
}> = t0 => {
  const $ = _c(9);
  const {
    locale
  } = t0;
  let t1;
  if ($[0] !== locale) {
    t1 = translate("loading", locale);
    $[0] = locale;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <span className="sr-only">{t1}</span>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  let t4;
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <Skeleton className="h-11 w-full bg-gray-200" />;
    t4 = <Skeleton className="h-32 w-full rounded-xl bg-gray-200" />;
    t5 = <Skeleton className="h-48 w-full rounded-xl bg-gray-200" />;
    $[4] = t3;
    $[5] = t4;
    $[6] = t5;
  } else {
    t3 = $[4];
    t4 = $[5];
    t5 = $[6];
  }
  let t6;
  if ($[7] !== t2) {
    t6 = <div className="space-y-4" role="status">{t2}{t3}{t4}{t5}</div>;
    $[7] = t2;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  return t6;
};
const Message: React.FC<{
  children: React.ReactNode;
}> = t0 => {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <Panel className="space-y-4 text-sm text-gray-700">{children}</Panel>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};

// next-auth navigates to Cevi.DB itself. With `redirect: false` it answers `undefined` whenever
// it cannot load its providers, a second tap during the first redirect for one, so there is no
// response to read a URL from.
const signInWithCeviDatabase = async (): Promise<void> => {
  await signIn('cevi-db', {
    callbackUrl: globalThis.location.href
  });
};

/** Where every form stands today, by form id. */
const useProgress = data => {
  const $ = _c(2);
  let t0;
  bb0: {
    if (data === undefined) {
      t0 = undefined;
      break bb0;
    }
    let t1;
    if ($[0] !== data.forms) {
      const now = new Date();
      t1 = Object.fromEntries(data.forms.map(form => [form.id, getSubmissionProgress({
        mode: form.mode,
        deadline: form.deadline,
        statuses: form.entries.map(_temp),
        closed: form.closed || form.finalized
      }, now)]));
      $[0] = data.forms;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    t0 = t1;
  }
  return t0;
};
const RetryMessage: React.FC<{
  locale: Locale;
  onRetry: () => void;
}> = t0 => {
  const $ = _c(12);
  const {
    locale,
    onRetry
  } = t0;
  let t1;
  if ($[0] !== locale) {
    t1 = translate(globalThis.navigator.onLine ? "loadError" : "offline", locale);
    $[0] = locale;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <p>{t1}</p>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== locale) {
    t3 = translate("retry", locale);
    $[4] = locale;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== onRetry || $[7] !== t3) {
    t4 = <button type="button" className={SECONDARY_BUTTON_CLASS} onClick={onRetry}>{t3}</button>;
    $[6] = onRetry;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t2 || $[10] !== t4) {
    t5 = <Message>{t2}{t4}</Message>;
    $[9] = t2;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
};
const DashboardForHof: React.FC<{
  hofId: string;
  locale: Locale;
}> = t0 => {
  const $ = _c(54);
  const {
    hofId,
    locale
  } = t0;
  const [tab, setTab] = useTabInAddress(TAB_IDS, "overview");
  const [scrollTarget, setScrollTarget] = useState();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => setScrollTarget(undefined);
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const clearScrollTarget = t1;
  const root = useRef(null);
  useScrollToForm(root, scrollTarget, clearScrollTarget);
  let t2;
  if ($[1] !== hofId || $[2] !== locale) {
    t2 = {
      hofId,
      locale
    };
    $[1] = hofId;
    $[2] = locale;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      staleTime: 30000,
      refetchOnMount: true,
      refetchOnWindowFocus: true,
      meta: {
        persist: false
      }
    };
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const dashboard = trpc.hofDashboard.getHofDashboard.useQuery(t2, t3);
  const progress = useProgress(dashboard.data);
  if (dashboard.isLoading) {
    let t4;
    if ($[5] !== locale) {
      t4 = <LoadingState locale={locale} />;
      $[5] = locale;
      $[6] = t4;
    } else {
      t4 = $[6];
    }
    return t4;
  }
  if (dashboard.data === undefined || progress === undefined) {
    let t4;
    if ($[7] !== dashboard) {
      t4 = () => void dashboard.refetch();
      $[7] = dashboard;
      $[8] = t4;
    } else {
      t4 = $[8];
    }
    let t5;
    if ($[9] !== locale || $[10] !== t4) {
      t5 = <RetryMessage locale={locale} onRetry={t4} />;
      $[9] = locale;
      $[10] = t4;
      $[11] = t5;
    } else {
      t5 = $[11];
    }
    return t5;
  }
  const data = dashboard.data;
  let t4;
  if ($[12] !== setTab) {
    t4 = (area, formId) => {
      setTab(area);
      setScrollTarget(formId);
    };
    $[12] = setTab;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== data || $[15] !== locale || $[16] !== progress || $[17] !== t4) {
    t5 = <OverviewView data={data} progress={progress} locale={locale} onOpen={t4} />;
    $[14] = data;
    $[15] = locale;
    $[16] = progress;
    $[17] = t4;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[19] !== data || $[20] !== locale || $[21] !== progress) {
    t6 = <AreaView area="infrastructure" data={data} progress={progress} locale={locale} />;
    t7 = <AreaView area="program" data={data} progress={progress} locale={locale} />;
    t8 = <AreaView area="material" data={data} progress={progress} locale={locale} />;
    t9 = <DocumentsView data={data} progress={progress} locale={locale} />;
    $[19] = data;
    $[20] = locale;
    $[21] = progress;
    $[22] = t6;
    $[23] = t7;
    $[24] = t8;
    $[25] = t9;
  } else {
    t6 = $[22];
    t7 = $[23];
    t8 = $[24];
    t9 = $[25];
  }
  let t10;
  if ($[26] !== t5 || $[27] !== t6 || $[28] !== t7 || $[29] !== t8 || $[30] !== t9) {
    t10 = {
      overview: t5,
      infrastructure: t6,
      program: t7,
      material: t8,
      documents: t9
    };
    $[26] = t5;
    $[27] = t6;
    $[28] = t7;
    $[29] = t8;
    $[30] = t9;
    $[31] = t10;
  } else {
    t10 = $[31];
  }
  const panels = t10;
  let t11;
  if ($[32] !== tab) {
    t11 = TABS.findIndex(t12 => {
      const {
        id
      } = t12;
      return id === tab;
    });
    $[32] = tab;
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  let t12;
  if ($[34] !== setTab) {
    t12 = index => setTab(TABS[index]?.id ?? "overview");
    $[34] = setTab;
    $[35] = t12;
  } else {
    t12 = $[35];
  }
  let t13;
  if ($[36] !== data || $[37] !== locale || $[38] !== progress) {
    t13 = TABS.map(t14 => {
      const {
        id: id_0,
        label
      } = t14;
      const open = data.forms.filter(form => {
        const formProgress = progress[form.id];
        return form.area === id_0 && formProgress !== undefined && isOpen(formProgress);
      }).length;
      return {
        label: label[locale],
        open,
        openLabel: translate("openCount", locale, {
          n: open
        })
      };
    });
    $[36] = data;
    $[37] = locale;
    $[38] = progress;
    $[39] = t13;
  } else {
    t13 = $[39];
  }
  let t14;
  if ($[40] !== locale) {
    t14 = translate("tabs", locale);
    $[40] = locale;
    $[41] = t14;
  } else {
    t14 = $[41];
  }
  let t15;
  if ($[42] !== t13 || $[43] !== t14) {
    t15 = <DashboardTabList tabs={t13} label={t14} />;
    $[42] = t13;
    $[43] = t14;
    $[44] = t15;
  } else {
    t15 = $[44];
  }
  let t16;
  if ($[45] !== panels) {
    t16 = TABS.map(t17 => {
      const {
        id: id_1
      } = t17;
      return <TabPanel key={id_1} unmount={false}>{panels[id_1]}</TabPanel>;
    });
    $[45] = panels;
    $[46] = t16;
  } else {
    t16 = $[46];
  }
  let t17;
  if ($[47] !== t16) {
    t17 = <TabPanels>{t16}</TabPanels>;
    $[47] = t16;
    $[48] = t17;
  } else {
    t17 = $[48];
  }
  let t18;
  if ($[49] !== t11 || $[50] !== t12 || $[51] !== t15 || $[52] !== t17) {
    t18 = <div ref={root}><TabGroup selectedIndex={t11} onChange={t12} className="space-y-6">{t15}{t17}</TabGroup></div>;
    $[49] = t11;
    $[50] = t12;
    $[51] = t15;
    $[52] = t17;
    $[53] = t18;
  } else {
    t18 = $[53];
  }
  return t18;
};
const DashboardForUser: React.FC<{
  locale: Locale;
}> = t0 => {
  const $ = _c(42);
  const {
    locale
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      refetchOnMount: true,
      meta: {
        persist: false
      }
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const hoefe = trpc.hofDashboard.getMyHofList.useQuery(undefined, t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      staleTime: 30000,
      refetchOnMount: true,
      refetchOnWindowFocus: true,
      meta: {
        persist: false
      }
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const overview = trpc.hofDashboard.getHofOverview.useQuery(undefined, t2);
  const [selectedHofId, setSelectedHofId] = useRememberedHofId();
  const selectId = useId();
  const dashboardStart = useRef(null);
  if (hoefe.isLoading) {
    let t3;
    if ($[2] !== locale) {
      t3 = <LoadingState locale={locale} />;
      $[2] = locale;
      $[3] = t3;
    } else {
      t3 = $[3];
    }
    return t3;
  }
  if (hoefe.data === undefined) {
    let t3;
    if ($[4] !== hoefe) {
      t3 = () => void hoefe.refetch();
      $[4] = hoefe;
      $[5] = t3;
    } else {
      t3 = $[5];
    }
    let t4;
    if ($[6] !== locale || $[7] !== t3) {
      t4 = <RetryMessage locale={locale} onRetry={t3} />;
      $[6] = locale;
      $[7] = t3;
      $[8] = t4;
    } else {
      t4 = $[8];
    }
    return t4;
  }
  if (hoefe.data.length === 0) {
    let t3;
    if ($[9] !== locale) {
      t3 = translate("noAccess", locale);
      $[9] = locale;
      $[10] = t3;
    } else {
      t3 = $[10];
    }
    let t4;
    if ($[11] !== t3) {
      t4 = <Message><p>{t3}</p></Message>;
      $[11] = t3;
      $[12] = t4;
    } else {
      t4 = $[12];
    }
    return t4;
  }
  let t3;
  if ($[13] !== hoefe.data || $[14] !== selectedHofId) {
    t3 = hoefe.data.find(candidate => candidate.id === selectedHofId) ?? hoefe.data[0];
    $[13] = hoefe.data;
    $[14] = selectedHofId;
    $[15] = t3;
  } else {
    t3 = $[15];
  }
  const hof = t3;
  if (hof === undefined) {
    let t4;
    if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <></>;
      $[16] = t4;
    } else {
      t4 = $[16];
    }
    return t4;
  }
  let t4;
  if ($[17] !== locale) {
    t4 = () => !hasUnsavedWork() || globalThis.confirm(translate("discardUnsaved", locale));
    $[17] = locale;
    $[18] = t4;
  } else {
    t4 = $[18];
  }
  const maySwitch = t4;
  let t5;
  if ($[19] !== hof.id || $[20] !== locale || $[21] !== maySwitch || $[22] !== overview.data || $[23] !== setSelectedHofId) {
    t5 = overview.data?.isReviewer === true && <HofOverviewTable hoefe={overview.data.hoefe} selectedHofId={hof.id} locale={locale} onOpen={hofId => {
      if (hofId !== hof.id && !maySwitch()) {
        return;
      }
      setSelectedHofId(hofId);
      dashboardStart.current?.scrollIntoView({
        behavior: scrollBehavior(),
        block: "start"
      });
    }} />;
    $[19] = hof.id;
    $[20] = locale;
    $[21] = maySwitch;
    $[22] = overview.data;
    $[23] = setSelectedHofId;
    $[24] = t5;
  } else {
    t5 = $[24];
  }
  let t6;
  if ($[25] !== hoefe.data || $[26] !== hof.id || $[27] !== hof.name || $[28] !== locale || $[29] !== maySwitch || $[30] !== selectId || $[31] !== setSelectedHofId) {
    t6 = hoefe.data.length === 1 ? <h2 className="font-heading text-conveniat-green text-2xl font-extrabold">{hof.name}</h2> : <div className="space-y-1"><h2 className="sr-only">{hof.name}</h2><label htmlFor={selectId} className="text-sm font-medium text-gray-600">{translate("hof", locale)}</label><Select value={hof.id} onValueChange={hofId_0 => {
        if (maySwitch()) {
          setSelectedHofId(hofId_0);
        }
      }}><SelectTrigger id={selectId} className="font-heading text-conveniat-green h-12 w-full max-w-sm bg-white text-lg font-extrabold focus-visible:ring-2 focus-visible:ring-green-600"><SelectValue /></SelectTrigger><SelectContent className="max-w-(--radix-select-content-available-width) bg-white">{hoefe.data.map(_temp2)}</SelectContent></Select></div>;
    $[25] = hoefe.data;
    $[26] = hof.id;
    $[27] = hof.name;
    $[28] = locale;
    $[29] = maySwitch;
    $[30] = selectId;
    $[31] = setSelectedHofId;
    $[32] = t6;
  } else {
    t6 = $[32];
  }
  let t7;
  if ($[33] !== hof.id || $[34] !== locale) {
    t7 = <DashboardForHof key={hof.id} hofId={hof.id} locale={locale} />;
    $[33] = hof.id;
    $[34] = locale;
    $[35] = t7;
  } else {
    t7 = $[35];
  }
  let t8;
  if ($[36] !== t6 || $[37] !== t7) {
    t8 = <div ref={dashboardStart} className="scroll-mt-24 space-y-6">{t6}{t7}</div>;
    $[36] = t6;
    $[37] = t7;
    $[38] = t8;
  } else {
    t8 = $[38];
  }
  let t9;
  if ($[39] !== t5 || $[40] !== t8) {
    t9 = <div className="space-y-6">{t5}{t8}</div>;
    $[39] = t5;
    $[40] = t8;
    $[41] = t9;
  } else {
    t9 = $[41];
  }
  return t9;
};

/**
 * Who is signed in, and the way out: the dashboard shows one person's Höfe, so on a shared
 * phone the next person needs to see whose they are and switch.
 */
const SignedInLine: React.FC<{
  name: string;
  locale: Locale;
}> = t0 => {
  const $ = _c(16);
  const {
    name,
    locale
  } = t0;
  const releasePushSubscriptions = useReleasePushSubscriptions();
  let t1;
  if ($[0] !== locale || $[1] !== name) {
    t1 = translate("signedInAs", locale, {
      name
    });
    $[0] = locale;
    $[1] = name;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== t1) {
    t2 = <span>{t1}</span>;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== releasePushSubscriptions) {
    t3 = () => {
      releasePushSubscriptions().then(_temp3);
    };
    $[5] = releasePushSubscriptions;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <LogOut className="h-4 w-4" aria-hidden={true} />;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== locale) {
    t5 = translate("signOut", locale);
    $[8] = locale;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t3 || $[11] !== t5) {
    t6 = <button type="button" className="text-conveniat-green inline-flex min-h-10 cursor-pointer items-center gap-1.5 font-semibold underline-offset-2 hover:underline" onClick={t3}>{t4}{t5}</button>;
    $[10] = t3;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t2 || $[14] !== t6) {
    t7 = <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 text-sm text-gray-600">{t2}{t6}</div>;
    $[13] = t2;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  return t7;
};
const HofDashboardContent: React.FC = () => {
  const $ = _c(21);
  const locale = useCurrentLocale(i18nConfig) as Locale;
  const {
    status,
    data: session
  } = useSession();
  if (status === "loading") {
    let t0;
    if ($[0] !== locale) {
      t0 = <LoadingState locale={locale} />;
      $[0] = locale;
      $[1] = t0;
    } else {
      t0 = $[1];
    }
    return t0;
  }
  if (status === "unauthenticated") {
    let t0;
    if ($[2] !== locale) {
      t0 = translate("loginRequired", locale);
      $[2] = locale;
      $[3] = t0;
    } else {
      t0 = $[3];
    }
    let t1;
    if ($[4] !== t0) {
      t1 = <p>{t0}</p>;
      $[4] = t0;
      $[5] = t1;
    } else {
      t1 = $[5];
    }
    let t2;
    if ($[6] !== locale) {
      t2 = translate("login", locale);
      $[6] = locale;
      $[7] = t2;
    } else {
      t2 = $[7];
    }
    let t3;
    if ($[8] !== t2) {
      t3 = <button type="button" className={PRIMARY_BUTTON_CLASS} onClick={_temp4}>{t2}</button>;
      $[8] = t2;
      $[9] = t3;
    } else {
      t3 = $[9];
    }
    let t4;
    if ($[10] !== t1 || $[11] !== t3) {
      t4 = <Message>{t1}{t3}</Message>;
      $[10] = t1;
      $[11] = t3;
      $[12] = t4;
    } else {
      t4 = $[12];
    }
    return t4;
  }
  const user = session?.user;
  const name = user?.name ?? user?.email ?? "";
  let t0;
  if ($[13] !== locale || $[14] !== name) {
    t0 = name !== "" && <SignedInLine name={name} locale={locale} />;
    $[13] = locale;
    $[14] = name;
    $[15] = t0;
  } else {
    t0 = $[15];
  }
  let t1;
  if ($[16] !== locale) {
    t1 = <DashboardForUser locale={locale} />;
    $[16] = locale;
    $[17] = t1;
  } else {
    t1 = $[17];
  }
  let t2;
  if ($[18] !== t0 || $[19] !== t1) {
    t2 = <div className="space-y-6">{t0}{t1}</div>;
    $[18] = t0;
    $[19] = t1;
    $[20] = t2;
  } else {
    t2 = $[20];
  }
  return t2;
};

/**
 * The Hof dashboard as an editor places it on a page: the address administrators of a Hof
 * hand in its plans, order material and follow what the Ressorts say. Signed-out visitors are
 * asked to sign in; nothing is fetched for them, since the tRPC client answers a 401 with a
 * sign out and a redirect.
 *
 * Laid out by container queries: the block sits in a content column far narrower than the
 * viewport, and possibly in a two-column block.
 */
export const HofDashboard: React.FC = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="@container"><HofDashboardContent /></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
function _temp(entry) {
  return entry.status;
}
function _temp2(candidate_0) {
  return <SelectItem key={candidate_0.id} value={candidate_0.id} className="min-h-11 whitespace-normal">{candidate_0.name}</SelectItem>;
}
function _temp3() {
  flushPersonalData({
    clearCachedPages: true
  });
  return signOut({
    redirectTo: globalThis.location.href
  });
}
function _temp4() {
  return void signInWithCeviDatabase();
}